import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useMsal } from '@/azure/useMsal'
import { useAppConfigStore } from '@/stores/app-config-store'
import { Client } from '@/interfaces/client'

export function useAutoClientNavigation() {
  const router = useRouter()
  const { clientRoles, isAuthenticated } = useMsal()
  const appConfigStore = useAppConfigStore()

  const availableClients = computed(() => {
    if (!clientRoles.value) {
      return []
    }

    // Check if app configs are loaded
    const clientDataMap = appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP
    if (!clientDataMap || Object.keys(clientDataMap).length === 0) {
      return []
    }

    const allClients = Object.entries(clientDataMap).map(([uriSegment, clientData]) => ({
      clientCode: clientData.clientCode,
      clientName: clientData.clientName,
      status: clientData.status,
      uriSegment,
    })) as (Client & { uriSegment: string })[]

    // Use clientRoles to set availableClients
    if (clientRoles.value?.['pw']?.includes('ADMIN')) {
      return allClients
    } else {
      const filtered = allClients.filter((client) => {
        return Object.keys(clientRoles.value ?? {}).includes(client.clientCode ?? '')
      })
      return filtered
    }
  })

  const checkAndNavigateToSingleClient = () => {
    if (!isAuthenticated.value || !clientRoles.value) {
      return
    }

    const clients = availableClients.value

    // If there's exactly one available client, navigate to it
    if (clients.length === 1) {
      const client = clients[0]
      const currentPath = router.currentRoute.value.path
      const currentClientCode = router.currentRoute.value.params?.clientCode

      // Already on the target client route.
      if (currentClientCode === client.uriSegment) {
        return
      }

      // Only navigate if we're currently on the home page to avoid interrupting other navigation
      if (currentPath === '/home' || currentPath === '/') {
        router.push(`/${client.uriSegment}`)
      }
    }
  }

  // Watch for authentication, client roles, and app config changes
  watch(
    [isAuthenticated, clientRoles, () => appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP],
    checkAndNavigateToSingleClient,
    { immediate: true },
  )

  // Watch for route changes to handle post-authentication navigation
  watch(
    () => router.currentRoute.value.path,
    (newPath) => {
      // Trigger auto-navigation when user lands on home page
      if (newPath === '/home' || newPath === '/') {
        checkAndNavigateToSingleClient()
      }
    },
  )

  return {
    availableClients,
    checkAndNavigateToSingleClient,
  }
}
